import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import BreadcrumbNav from "@/components/base/BreadcrumbNav";
import { articles, categoryInfo, type Article } from "@/mocks/articleData";

const categorySlugs = Object.keys(categoryInfo);

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || categorySlugs[0]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);

  useEffect(() => {
    if (slug && categoryInfo[slug]) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    setFilteredArticles(articles.filter((a) => a.category === activeCategory));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeCategory]);

  const category = categoryInfo[activeCategory];

  if (!category) {
    return (
      <Layout>
        <div className="pt-20 pb-20 text-center">
          <p className="text-foreground-600">分类不存在</p>
          <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <BreadcrumbNav
              items={[
                { label: "首页", path: "/" },
                { label: category.name },
              ]}
            />
          </div>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
              {category.name}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base">{category.description}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8">
            {categorySlugs.map((catSlug) => {
              const cat = categoryInfo[catSlug];
              return (
                <Link
                  key={catSlug}
                  to={`/category/${catSlug}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === catSlug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group p-5 rounded-2xl bg-background-100 hover:bg-background-100/80 transition-all duration-200 flex flex-col cursor-pointer"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-foreground-500">{article.date}</span>
                  <span className="text-xs text-foreground-400">·</span>
                  <span className="text-xs text-foreground-500">阅读 {article.readTime}</span>
                  {article.isHot && (
                    <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                      热门
                    </span>
                  )}
                  {article.isNew && (
                    <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                      最新
                    </span>
                  )}
                </div>
                <h2 className="font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-2 text-sm md:text-base">
                  {article.title}
                </h2>
                <p className="text-sm text-foreground-600 line-clamp-2 flex-1">{article.excerpt}</p>
              </Link>
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-16">
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${category.name} - 爱情岛网址导航`,
            description: category.description,
            url: `https://aiqingdao.com/category/${activeCategory}`,
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://aiqingdao.com" },
                { "@type": "ListItem", position: 2, name: category.name },
              ],
            },
          }),
        }}
      />
    </Layout>
  );
}